export type PortalRequestType = 'improvement' | 'change' | 'question' | 'support';

export type PortalRequestPriority = 'high' | 'medium' | 'low';

export type PortalRequestStatus =
  | 'awaitingApproval'
  | 'inProgress'
  | 'analysis'
  | 'answered'
  | 'completed'
  | 'cancelled';

export type PortalRequestComment = {
  id: string;
  authorName: string;
  body: string;
  whenLabel: string;
};

export type PortalRequestAttachment = {
  id: string;
  name: string;
  sizeLabel: string;
  dateLabel: string;
  downloadUrl: string;
};

export type PortalRequestLink = {
  id: string;
  kindLabel: string;
  title: string;
};

export type PortalRequestHistoryItem = {
  id: string;
  title: string;
  detail: string;
  whenLabel: string;
};

export type PortalRequestItem = {
  id: string;
  number: string;
  title: string;
  description: string;
  type: PortalRequestType;
  priority: PortalRequestPriority;
  status: PortalRequestStatus;
  projectId: string;
  projectName: string;
  createdAtLabel: string;
  updatedAtLabel: string;
  requestedByName: string;
  requestedByRole: string;
  ownerName: string;
  ownerRole: string;
  impactSchedule?: string;
  impactCost?: string;
  comments: PortalRequestComment[];
  attachments: PortalRequestAttachment[];
  links: PortalRequestLink[];
  history: PortalRequestHistoryItem[];
};

export type PortalRequestsSummary = {
  total: number;
  inProgress: number;
  awaitingApproval: number;
  completed: number;
  cancelled: number;
};

export type PortalRequestsData = {
  summary: PortalRequestsSummary;
  projects: { id: string; name: string }[];
  items: PortalRequestItem[];
};

export const portalRequestsMock: PortalRequestsData = {
  summary: {
    total: 12,
    inProgress: 5,
    awaitingApproval: 4,
    completed: 2,
    cancelled: 1,
  },
  projects: [
    { id: 'crm', name: 'Implementação CRM' },
    { id: 'dashboard-comercial', name: 'Dashboard Comercial' },
    { id: 'integracao-erp', name: 'Integração ERP' },
    { id: 'portal-cliente', name: 'Portal do Cliente' },
  ],
  items: [
    {
      id: 'req-015',
      number: '015',
      title: 'Incluir novo indicador no dashboard',
      description:
        'Adicionar KPI de conversão por etapa no painel gerencial, com filtros por período e equipe comercial.',
      type: 'improvement',
      priority: 'high',
      status: 'awaitingApproval',
      projectId: 'crm',
      projectName: 'Implementação CRM',
      createdAtLabel: '20/10/2026 09:14',
      updatedAtLabel: '27/10/2026 16:40',
      requestedByName: 'Ana Souza',
      requestedByRole: 'Patrocinadora',
      ownerName: 'Rafael Oliveira',
      ownerRole: 'Gerente do projeto',
      impactSchedule: '+ 5 dias',
      impactCost: 'R$ 8.000',
      comments: [
        {
          id: 'c1',
          authorName: 'Rafael Oliveira',
          body: 'Impacto estimado e proposta enviados para aprovação.',
          whenLabel: '27/10/2026 16:40',
        },
        {
          id: 'c2',
          authorName: 'Ana Souza',
          body: 'Prioridade alta para a campanha do trimestre.',
          whenLabel: '21/10/2026 11:02',
        },
      ],
      attachments: [
        {
          id: 'a1',
          name: 'indicadores_funil.xlsx',
          sizeLabel: '245 KB',
          dateLabel: '21/10/2026',
          downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        },
        {
          id: 'a2',
          name: 'mockup_dashboard.png',
          sizeLabel: '1.2 MB',
          dateLabel: '22/10/2026',
          downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        },
      ],
      links: [
        { id: 'l1', kindLabel: 'Atividade', title: 'Dashboard gerencial' },
        { id: 'l2', kindLabel: 'Entrega', title: 'Painel comercial v1.2' },
        { id: 'l3', kindLabel: 'Documento', title: 'Requisitos do dashboard' },
      ],
      history: [
        {
          id: 'h1',
          title: 'Aguardando aprovação',
          detail: 'Impacto analisado pela equipe Baruk',
          whenLabel: '27/10/2026 16:40',
        },
        {
          id: 'h2',
          title: 'Solicitação aberta',
          detail: 'Registrada por Ana Souza',
          whenLabel: '20/10/2026 09:14',
        },
      ],
    },
    {
      id: 'req-014',
      number: '014',
      title: 'Ampliar escopo da integração de estoque',
      description: 'Incluir saldos por depósito na sincronização com o ERP.',
      type: 'change',
      priority: 'high',
      status: 'inProgress',
      projectId: 'integracao-erp',
      projectName: 'Integração ERP',
      createdAtLabel: '18/10/2026 14:20',
      updatedAtLabel: '26/10/2026 10:05',
      requestedByName: 'João Santos',
      requestedByRole: 'Patrocinador',
      ownerName: 'Carlos Mendes',
      ownerRole: 'Analista de integração',
      impactSchedule: '+ 8 dias',
      impactCost: 'R$ 12.000',
      comments: [],
      attachments: [],
      links: [{ id: 'l1', kindLabel: 'Atividade', title: 'Integração de estoque' }],
      history: [
        {
          id: 'h1',
          title: 'Em andamento',
          detail: 'Análise técnica iniciada',
          whenLabel: '26/10/2026 10:05',
        },
      ],
    },
    {
      id: 'req-013',
      number: '013',
      title: 'Dúvida sobre permissões do portal',
      description: 'Como restringir o acesso a documentos contratuais apenas para administradores?',
      type: 'question',
      priority: 'medium',
      status: 'answered',
      projectId: 'portal-cliente',
      projectName: 'Portal do Cliente',
      createdAtLabel: '17/10/2026 08:45',
      updatedAtLabel: '25/10/2026 17:10',
      requestedByName: 'Fernanda Lima',
      requestedByRole: 'Administradora',
      ownerName: 'Mariana Silva',
      ownerRole: 'Consultora',
      comments: [
        {
          id: 'c1',
          authorName: 'Mariana Silva',
          body: 'É possível via perfil Administrador na aba Minha Conta > Permissões.',
          whenLabel: '25/10/2026 17:10',
        },
      ],
      attachments: [],
      links: [],
      history: [
        {
          id: 'h1',
          title: 'Respondida',
          detail: 'Resposta publicada por Mariana Silva',
          whenLabel: '25/10/2026 17:10',
        },
      ],
    },
    {
      id: 'req-012',
      number: '012',
      title: 'Erro ao exportar relatório de funil',
      description: 'Exportação em Excel retorna arquivo vazio em alguns filtros.',
      type: 'support',
      priority: 'high',
      status: 'inProgress',
      projectId: 'dashboard-comercial',
      projectName: 'Dashboard Comercial',
      createdAtLabel: '16/10/2026 11:30',
      updatedAtLabel: '24/10/2026 09:18',
      requestedByName: 'Bruno Costa',
      requestedByRole: 'Analista comercial',
      ownerName: 'Rafael Oliveira',
      ownerRole: 'Gerente do projeto',
      comments: [],
      attachments: [
        {
          id: 'a1',
          name: 'print_erro_export.png',
          sizeLabel: '640 KB',
          dateLabel: '16/10/2026',
          downloadUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        },
      ],
      links: [],
      history: [],
    },
    {
      id: 'req-011',
      number: '011',
      title: 'Incluir campo de prioridade no cadastro',
      description: 'Disponibilizar prioridade no formulário de oportunidades.',
      type: 'improvement',
      priority: 'medium',
      status: 'analysis',
      projectId: 'crm',
      projectName: 'Implementação CRM',
      createdAtLabel: '15/10/2026 16:02',
      updatedAtLabel: '23/10/2026 14:55',
      requestedByName: 'Ana Souza',
      requestedByRole: 'Patrocinadora',
      ownerName: 'Mariana Silva',
      ownerRole: 'Consultora',
      impactSchedule: '+ 2 dias',
      impactCost: 'R$ 3.500',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
    {
      id: 'req-010',
      number: '010',
      title: 'Adiar go-live em 1 semana',
      description: 'Necessário alinhar janela com a campanha comercial do trimestre.',
      type: 'change',
      priority: 'high',
      status: 'awaitingApproval',
      projectId: 'integracao-erp',
      projectName: 'Integração ERP',
      createdAtLabel: '14/10/2026 10:10',
      updatedAtLabel: '22/10/2026 18:20',
      requestedByName: 'João Santos',
      requestedByRole: 'Patrocinador',
      ownerName: 'Carlos Mendes',
      ownerRole: 'Analista de integração',
      impactSchedule: '+ 7 dias',
      impactCost: 'Não estimado',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
    {
      id: 'req-009',
      number: '009',
      title: 'Como configurar alertas de SLA?',
      description: 'Precisamos de orientação para alertas de atraso em atividades críticas.',
      type: 'question',
      priority: 'low',
      status: 'completed',
      projectId: 'portal-cliente',
      projectName: 'Portal do Cliente',
      createdAtLabel: '10/10/2026 09:00',
      updatedAtLabel: '20/10/2026 12:30',
      requestedByName: 'Fernanda Lima',
      requestedByRole: 'Administradora',
      ownerName: 'Mariana Silva',
      ownerRole: 'Consultora',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
    {
      id: 'req-008',
      number: '008',
      title: 'Corrigir validação de CPF no cadastro',
      description: 'Ajuste na regra de validação que bloqueia cadastros válidos.',
      type: 'support',
      priority: 'medium',
      status: 'completed',
      projectId: 'crm',
      projectName: 'Implementação CRM',
      createdAtLabel: '08/10/2026 13:40',
      updatedAtLabel: '19/10/2026 15:05',
      requestedByName: 'Fernanda Lima',
      requestedByRole: 'Administradora',
      ownerName: 'Rafael Oliveira',
      ownerRole: 'Gerente do projeto',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
    {
      id: 'req-007',
      number: '007',
      title: 'Treinamento adicional da equipe comercial',
      description: 'Solicitar uma turma extra de capacitação no CRM.',
      type: 'support',
      priority: 'medium',
      status: 'inProgress',
      projectId: 'crm',
      projectName: 'Implementação CRM',
      createdAtLabel: '07/10/2026 11:15',
      updatedAtLabel: '18/10/2026 09:40',
      requestedByName: 'Ana Souza',
      requestedByRole: 'Patrocinadora',
      ownerName: 'Ana Souza',
      ownerRole: 'Patrocinadora',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
    {
      id: 'req-006',
      number: '006',
      title: 'Alterar layout do relatório semanal',
      description: 'Incluir comparação mês a mês no PDF executivo.',
      type: 'improvement',
      priority: 'low',
      status: 'awaitingApproval',
      projectId: 'dashboard-comercial',
      projectName: 'Dashboard Comercial',
      createdAtLabel: '05/10/2026 16:50',
      updatedAtLabel: '17/10/2026 08:12',
      requestedByName: 'Bruno Costa',
      requestedByRole: 'Analista comercial',
      ownerName: 'Rafael Oliveira',
      ownerRole: 'Gerente do projeto',
      impactSchedule: '+ 3 dias',
      impactCost: 'R$ 4.200',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
    {
      id: 'req-005',
      number: '005',
      title: 'Acesso ao ambiente de homologação',
      description: 'Liberar usuários do time financeiro no ambiente de testes.',
      type: 'support',
      priority: 'medium',
      status: 'inProgress',
      projectId: 'crm',
      projectName: 'Implementação CRM',
      createdAtLabel: '03/10/2026 10:25',
      updatedAtLabel: '16/10/2026 11:48',
      requestedByName: 'Carlos Mendes',
      requestedByRole: 'Analista',
      ownerName: 'Mariana Silva',
      ownerRole: 'Consultora',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
    {
      id: 'req-004',
      number: '004',
      title: 'Cancelar integração de marketing',
      description: 'Escopo fora da janela atual do projeto.',
      type: 'change',
      priority: 'low',
      status: 'cancelled',
      projectId: 'crm',
      projectName: 'Implementação CRM',
      createdAtLabel: '01/10/2026 09:05',
      updatedAtLabel: '12/10/2026 14:00',
      requestedByName: 'João Santos',
      requestedByRole: 'Patrocinador',
      ownerName: 'Rafael Oliveira',
      ownerRole: 'Gerente do projeto',
      comments: [],
      attachments: [],
      links: [],
      history: [],
    },
  ],
};
